import { useEffect, useState } from 'react';
import { Modal, Table, Tag, message, Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ClockCircleOutlined } from '@ant-design/icons';

import apiClient from '@/api/client';

interface OperationLog {
  id: number;
  target_type: string;
  target_type_display: string;
  target_id: number;
  action: string;
  action_display: string;
  operator: {
    username: string;
    display_name: string;
  } | null;
  operator_type: string;
  operator_type_display: string;
  source?: string;
  source_display?: string;
  description: string;
  before_data: Record<string, any> | null;
  after_data: Record<string, any> | null;
  created_at: string;
}

interface OperationLogModalProps {
  open: boolean;
  onCancel: () => void;
  targetType: 'user' | 'project' | 'package' | 'file' | 'file_type';
  targetId: number;
  title?: string;
}

const OperationLogModal = ({ open, onCancel, targetType, targetId, title }: OperationLogModalProps) => {
  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchLogs();
    }
  }, [open, targetType, targetId]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get<any>('/logs/', {
        params: {
          target_type: targetType,
          target_id: targetId,
        },
      });
      const logData = response.data.results || response.data;
      setLogs(Array.isArray(logData) ? logData : []);
    } catch (error) {
      message.error('获取操作日志失败');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action: string) => {
    const colors: Record<string, string> = {
      create: 'green',
      update: 'blue',
      delete: 'red',
      download: 'cyan',
      reset_password: 'orange',
      auto_delete: 'magenta',
    };
    return colors[action] || 'default';
  };

  const getSourceColor = (source: string) => {
    const colors: Record<string, string> = {
      bsp: 'blue',
      api: 'purple',
      bvp_bot: 'green',
      rpa: 'orange',
      system: 'default',
    };
    return colors[source] || 'default';
  };

  const renderChanges = (record: OperationLog) => {
    if (!record.before_data || !record.after_data) {
      return record.description;
    }

    const fieldLabels: Record<string, string> = {
      display_name: '显示名称',
      email: '邮箱',
      is_active: '启用状态',
      name: '名称',
      code: '编码',
      description: '描述',
      sort_order: '排序',
      vehicle_model: '车型',
      vehicle_subtype: '子车型',
      platform: '硬件平台',
      vehicle_models: '车型列表',
      vehicle_subtypes: '子车型列表',
      platforms: '硬件平台列表',
      version: '版本',
      status: '状态',
    };

    const formatValue = (value: unknown) => {
      if (Array.isArray(value)) return value.length ? value.join('、') : '空';
      if (value === undefined || value === null || value === '') return '空';
      return String(value);
    };

    const changes: string[] = [];
    for (const [field, oldValue] of Object.entries(record.before_data)) {
      const newValue = record.after_data[field];
      if (newValue !== undefined && JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        const label = fieldLabels[field] || field;

        if (field === 'is_active') {
          const oldDisplay = oldValue ? '启用' : '禁用';
          const newDisplay = newValue ? '启用' : '禁用';
          changes.push(`${label}: ${oldDisplay} → ${newDisplay}`);
        } else {
          changes.push(`${label}: ${formatValue(oldValue)} → ${formatValue(newValue)}`);
        }
      }
    }

    if (changes.length > 0) {
      return (
        <div>
          <div>{record.description}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            {changes.join(', ')}
          </div>
        </div>
      );
    }

    return record.description;
  };

  const columns: ColumnsType<OperationLog> = [
    {
      title: '操作时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '操作类型',
      dataIndex: 'action_display',
      key: 'action',
      width: 100,
      render: (text, record) => (
        <Tag color={getActionColor(record.action)}>{text}</Tag>
      ),
    },
    {
      title: '操作人',
      key: 'operator',
      width: 150,
      render: (_, record) => {
        if (!record.operator) {
          return <span style={{ color: '#999' }}>系统</span>;
        }
        return (
          <div>
            <div>{record.operator.display_name || record.operator.username}</div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              {record.operator_type_display}
            </div>
          </div>
        );
      },
    },
    {
      title: '来源',
      key: 'source',
      width: 110,
      render: (_, record) => {
        if (!record.source) return <span style={{ color: '#999' }}>-</span>;
        return (
          <Tag color={getSourceColor(record.source)}>
            {record.source_display || record.source}
          </Tag>
        );
      },
    },
    {
      title: '操作描述',
      key: 'description',
      ellipsis: true,
      render: (_, record) => renderChanges(record),
    },
  ];

  return (
    <Modal
      title={
        <span>
          <ClockCircleOutlined style={{ marginRight: '8px' }} />
          {title || '操作日志'}
        </span>
      }
      open={open}
      onCancel={onCancel}
      width={900}
      footer={null}
    >
      {logs.length === 0 && !loading ? (
        <Empty
          description="暂无操作记录"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      )}
    </Modal>
  );
};

export default OperationLogModal;
